import { createContext, useCallback, useContext, useState } from 'react';

/** BOQ inspector/data-sheet transition: near-instant, but still perceptible. */
export const BOQ_PANEL_MOTION_DURATION_MS = 80;

export interface AppRightPanelContextValue {
  claimLeftShellInspector: (id: string, onClose: () => void) => void;
  releaseLeftShellInspector: (id: string) => void;
  activeLeftShellInspectorId: string | null;
  claimShellInspector: (id: string, onClose: () => void) => void;
  releaseShellInspector: (id: string) => void;
  activeShellInspectorId: string | null;
  /** Explicitly bring a retained page inspector back from a global panel. */
  showShellInspector: () => void;
  isShellInspectorOverridden: boolean;

  // Project detail inspectors use the same shell slot as the BOQ and Takeoff.
  claimSitePhotoPanel: () => void;
  releaseSitePhotoPanel: () => void;
  isSitePhotoPanelActive: boolean;
  claimProjectTaskPanel: () => void;
  releaseProjectTaskPanel: () => void;
  isProjectTaskPanelActive: boolean;
  claimProjectMemberPanel: () => void;
  releaseProjectMemberPanel: () => void;
  isProjectMemberPanelActive: boolean;
  // ── Right panel (side panel) ───────────────────────────────────────────────
  claimBoqPanel: () => void;
  releaseBoqPanel: () => void;
  /**
   * True when the BOQ side panel has claimed the slot AND no notification/
   * message panel is overriding it.
   */
  isBoqPanelActive: boolean;

  // ── Bottom panel ──────────────────────────────────────────────────────────
  /** Called by the BOQ editor when its bottom panel opens. */
  claimBoqBottomPanel: () => void;
  releaseBoqBottomPanel: () => void;
  /** True when the BOQ bottom panel is the active bottom panel. */
  isBoqBottomPanelActive: boolean;
}

export const AppRightPanelContext = createContext<AppRightPanelContextValue>({
  claimLeftShellInspector: () => {},
  releaseLeftShellInspector: () => {},
  activeLeftShellInspectorId: null,
  claimShellInspector: () => {},
  releaseShellInspector: () => {},
  activeShellInspectorId: null,
  showShellInspector: () => {},
  isShellInspectorOverridden: false,
  claimSitePhotoPanel: () => {},
  releaseSitePhotoPanel: () => {},
  isSitePhotoPanelActive: false,
  claimProjectTaskPanel: () => {},
  releaseProjectTaskPanel: () => {},
  isProjectTaskPanelActive: false,
  claimProjectMemberPanel: () => {},
  releaseProjectMemberPanel: () => {},
  isProjectMemberPanelActive: false,
  claimBoqPanel: () => {},
  releaseBoqPanel: () => {},
  isBoqPanelActive: false,
  claimBoqBottomPanel: () => {},
  releaseBoqBottomPanel: () => {},
  isBoqBottomPanelActive: false,
});

export function useAppRightPanel(): AppRightPanelContextValue {
  return useContext(AppRightPanelContext);
}

interface AppRightPanelProviderProps {
  notificationOpen: boolean;
  messagePanelOpen: boolean;
  supportOpen?: boolean;
  onShowShellInspector?: () => void;
  children: (extra: {
    boqPanelOpen: boolean;
    boqBottomPanelOpen: boolean;
    sitePhotoPanelOpen: boolean;
    projectMemberPanelOpen: boolean;
    projectTaskPanelOpen: boolean;
    leftShellInspectorOpen: boolean;
    closeLeftShellInspector: () => void;
    shellInspectorOpen: boolean;
    closeShellInspector: () => void;
  }) => React.ReactNode;
}

export function AppRightPanelProvider({
  notificationOpen,
  messagePanelOpen,
  supportOpen = false,
  onShowShellInspector,
  children,
}: AppRightPanelProviderProps) {
  const [leftShellInspectors, setLeftShellInspectors] = useState<Array<{ id: string; onClose: () => void }>>([]);
  const claimLeftShellInspector = useCallback((id: string, onClose: () => void) => {
    setLeftShellInspectors(current => [...current.filter(entry => entry.id !== id), { id, onClose }]);
  }, []);
  const releaseLeftShellInspector = useCallback((id: string) => setLeftShellInspectors(current => current.filter(entry => entry.id !== id)), []);
  const topLeftInspector = leftShellInspectors.at(-1);
  const leftShellInspectorOpen = !!topLeftInspector;
  const activeLeftShellInspectorId = topLeftInspector?.id ?? null;
  const closeLeftShellInspector = () => topLeftInspector?.onClose();

  const [shellInspectors, setShellInspectors] = useState<Array<{ id: string; onClose: () => void }>>([]);
  const claimShellInspector = useCallback((id: string, onClose: () => void) => {
    setShellInspectors(current => [...current.filter(entry => entry.id !== id), { id, onClose }]);
  }, []);
  const releaseShellInspector = useCallback((id: string) => {
    setShellInspectors(current => current.filter(entry => entry.id !== id));
  }, []);
  const topInspector = shellInspectors.at(-1);
  const shellInspectorOpen = !!topInspector;
  const isShellInspectorOverridden = supportOpen || notificationOpen || messagePanelOpen;
  const showShellInspector = useCallback(() => onShowShellInspector?.(), [onShowShellInspector]);
  const activeShellInspectorId = !isShellInspectorOverridden
    ? topInspector?.id ?? null : null;
  const closeShellInspector = () => topInspector?.onClose();

  // Side panel (right)
  const [boqPanelOpen, setBoqPanelOpen] = useState(false);
  const claimBoqPanel = useCallback(() => setBoqPanelOpen(true), []);
  const releaseBoqPanel = useCallback(() => setBoqPanelOpen(false), []);
  const isBoqPanelActive = boqPanelOpen && !shellInspectorOpen && !notificationOpen && !messagePanelOpen;
  const [sitePhotoPanelOpen, setSitePhotoPanelOpen] = useState(false);
  const claimSitePhotoPanel = useCallback(() => setSitePhotoPanelOpen(true), []);
  const releaseSitePhotoPanel = useCallback(() => setSitePhotoPanelOpen(false), []);
  const isSitePhotoPanelActive = sitePhotoPanelOpen && !shellInspectorOpen && !supportOpen && !notificationOpen && !messagePanelOpen;
  const [projectMemberPanelOpen, setProjectMemberPanelOpen] = useState(false);
  const claimProjectMemberPanel = useCallback(() => setProjectMemberPanelOpen(true), []);
  const releaseProjectMemberPanel = useCallback(() => setProjectMemberPanelOpen(false), []);
  const isProjectMemberPanelActive = projectMemberPanelOpen && !shellInspectorOpen && !supportOpen && !notificationOpen && !messagePanelOpen;

  const [projectTaskPanelOpen, setProjectTaskPanelOpen] = useState(false);
  const claimProjectTaskPanel = useCallback(() => setProjectTaskPanelOpen(true), []);
  const releaseProjectTaskPanel = useCallback(() => setProjectTaskPanelOpen(false), []);
  const isProjectTaskPanelActive = projectTaskPanelOpen && !shellInspectorOpen && !supportOpen && !notificationOpen && !messagePanelOpen;

  // Bottom panel
  const [boqBottomPanelOpen, setBoqBottomPanelOpen] = useState(false);

  const claimBoqBottomPanel = useCallback(() => setBoqBottomPanelOpen(true), []);
  const releaseBoqBottomPanel = useCallback(() => setBoqBottomPanelOpen(false), []);
  const isBoqBottomPanelActive = boqBottomPanelOpen;

  return (
    <AppRightPanelContext.Provider
      value={{
        claimLeftShellInspector,
        releaseLeftShellInspector,
        activeLeftShellInspectorId,
        claimShellInspector,
        releaseShellInspector,
        activeShellInspectorId,
        showShellInspector,
        isShellInspectorOverridden,
        claimSitePhotoPanel,
        releaseSitePhotoPanel,
        isSitePhotoPanelActive,
        claimProjectTaskPanel,
        releaseProjectTaskPanel,
        isProjectTaskPanelActive,
        claimProjectMemberPanel,
        releaseProjectMemberPanel,
        isProjectMemberPanelActive,
        claimBoqPanel,
        releaseBoqPanel,
        isBoqPanelActive,
        claimBoqBottomPanel,
        releaseBoqBottomPanel,
        isBoqBottomPanelActive,
      }}
    >
      {children({ boqPanelOpen, boqBottomPanelOpen, sitePhotoPanelOpen, projectMemberPanelOpen, projectTaskPanelOpen, leftShellInspectorOpen, closeLeftShellInspector, shellInspectorOpen, closeShellInspector })}
    </AppRightPanelContext.Provider>
  );
}
